import {
    AssertionDescriptorDisplayValueTypeEnum,
    AssertionDescriptorUsageTypesEnum,
    AssertionDescriptorValueDisplayTypes,
    AssertionDescriptorValueSourceTypeEnum,
    EntityTypes,
    LookupDataEnums,
    NotePurposeTypesEnum,
    NoteStatusTypes,
    NoteTypes,
    OrganizationTypesEnum,
    UserRightsEnum,
} from '../../utilities/Constants';
import {
    IAssociatedOrganizationModel,
    IOrganizationSettingModel,
} from '../../interfaces/IOrganization';
import { ICaseAssertionModel, ICaseModel } from '../../interfaces/ICase';
import {
    ICaseExpertViewDetailModel,
    IViewCaseExpertModelWithDefaultEdit,
} from '../../interfaces/ICaseExpert';
import { INoteCategoryTypeModel, INoteModel } from '../../interfaces/INote';
import { cloneDeep, isEmpty } from 'lodash';
import { useEffect, useState } from 'react';

import { AdditionalFieldsDialog } from '../../components/case/dialogs/AdditionalFieldsDialog';
import ApiClient from '../../services/apiClient';
import Auth from '../../stores/authentication';
import Authorization from '../../stores/Authorization';
import { CaseAuditControl } from '../../components/shared/modals/CaseAuditControl';
import CaseDataCustomLogicHelper from '../../utilities/CaseDataCustomLogicHelper';
import { CaseDefenseCounselsModal } from '../../components/case/dialogs/CaseDefenseCounselsModal';
import { CaseExpertEditor } from '../../components/case/static-sections/case-experts/CaseExpertEditor';
import CaseFileCheckmateSelectHelper from '../../utilities/CaseFileCheckmateSelectHelper';
import { CaseFileHeader } from '../../components/case/CaseFileHeader';
import { CaseFileSection } from '../../components/case/CaseFileSection';
import { CaseFileSections } from '../../components/case/CaseFileSections';
import CaseHelper from '../../utilities/CaseHelper';
import { CaseNarrativeModal } from '../../components/case/dialogs/CaseNarrativeModal';
import { CheckmateDialog } from '../../components/shared/dialog';
import CheckmateSelect from '../../components/shared/CheckmateSelect';
import CheckmateSelectHelper from '../../utilities/CheckmateSelectHelper';
import Common from '../../stores/Common';
import { CreateCaseResolutionPlanRequest } from '../quad/case-resolution-admin/useGetCaseResolutionQuery';
import { DocumentTitle } from '../../components/shared/DocumentTitle';
import { DocumentTypeEnum } from '../../utilities/DbConstants';
import { IApiResponse } from '../../tpi.apiclient';
import { IAssertionDescriptorModel } from '../../interfaces/IAssertionDescriptor';
import { IMultiSelectOption } from '../../interfaces/ILookup';
import { IValidation } from '../../interfaces/IError';
import { Loader } from '../../components/shared/Loader';
import { LocalRoutes } from '../../utilities/LocalRoutes';
import { MainContentOverlayComponent } from '../../components/shared/MainContentOverlay';
import { NoteEditor } from '../../components/notes/note-editor/NoteEditor';
import NoteHelper from '../../utilities/NoteHelper';
import { PageWithAffixedHeader } from '../../shared/PageWithAffixedHeader';
import { SameCaseStatusType } from '../quad/same-cases-admin/manage/useGetSameCasesQuery';
import UIHelper from '../../utilities/UIHelper';
import { UnsavedChangesBlocker } from '../../shared/UnsavedChangesBlocker';
import ValidateUtils from '../../shared/validations';
import { showErrorToast } from '../../shared/ToastUtils';
import { uniqueId } from 'lodash';
import { useAuthContext } from '../../contexts/AuthContext';
import { useCaseFileContext } from '../../contexts/useCaseFileContext';
import { useParams } from 'react-router-dom';
import { useZoneContext } from '../../contexts/useZoneContext';

const _apiClient = new ApiClient();

export function CaseFile() {
    const { guid } = useParams<{ guid: string }>();
    const { user, isLoggedIn, isLoadingUser } = useAuthContext();
    const zoneContext = useZoneContext();

    const {
        assertionDescriptors,
        currentCase,
        currentCaseExpert,
        currentNarrativeCaseAssertion,
        currentNote,
        forceNoteEditorInReadOnlyMode,
        displaySections,
        handleAssertionDescriptorSearchableSingleSelectChange,
        handleChangeADTextValue,
        handleNarrativeChange,
        isReadOnlyMode,
        originalCase,
        setAssertionDescriptors,
        setCaseDisplayCategories,
        setCaseManagers,
        setCaseStatuses,
        setCurrentCase,
        setCurrentCaseExpert,
        setCurrentNarrativeCaseAssertion,
        setCurrentNote,
        setForceNoteEditorInReadOnlyMode,
        setDisplaySections,
        setIsReadOnlyMode,
        setOriginalCase,
        setShowAdditionalFieldsDialog,
        setStaticItems,
        setUser,
        setZoneDefenseCounsels,
        setZoneUsers,
        showAdditionalFieldsDialog,
    } = useCaseFileContext();

    // STATE

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isCaseLoaded, setIsCaseLoaded] = useState<boolean>(false);
    const [notesRefreshIdentifier, setNotesRefreshIdentifier] = useState<string>(uniqueId);
    const [validation, setValidation] = useState<IValidation>({});
    const [caseNoteCategoryTypes, setCaseNoteCategoryTypes] = useState<INoteCategoryTypeModel[]>(
        []
    );
    const [originalNotes, setOriginalNotes] = useState<INoteModel[]>([]);
    const [, setOrganizationSettings] = useState<IOrganizationSettingModel[]>([]);
    const [enableCaseFileEdit, setEnableCaseFileEdit] = useState<boolean>(false);
    const [enablePlanningExecution, setEnablePlanningExecution] = useState<boolean>(false);
    const [showDeleteCaseDialog, setShowDeleteCaseDialog] = useState<boolean>(false);
    const [showPendingSameCaseDeleteCaseDialog, setShowPendingSameCaseDeleteCaseDialog] =
        useState<boolean>(false);
    const [showCaseResolutionDialog, setShowCaseResolutionDialog] = useState<boolean>(false);
    const [showSetPriorityDialog, setShowSetPriorityDialog] = useState<boolean>(false);
    const [showDefenseCounselModal, setShowDefenseCounselModal] = useState<boolean>(false);
    const [showHistory, setShowHistoryModal] = useState<boolean>(false);
    const [showUnsavedChangesOnCancelDialog, setShowUnsavedChangesOnCancelDialog] =
        useState<boolean>(false);

    const isSystemAdmin = user ? Auth.isSystemAdmin(user) : false;

    const differences =
        !isEmpty(originalCase) && !isEmpty(Object.keys(currentCase))
            ? UIHelper.difference(cloneDeep(currentCase), cloneDeep(originalCase))
            : {};

    const hasUnsavedChanges = !isEmpty(differences);

    // SHOULD BE SERVICE METHODS
    const fetchCaseFileModel = async () => {
        const response = await zoneContext.zoneApiClient.getCaseFileModel();
        if (response.httpResponse.status === 401) {
            window.location.reload();
            return;
        }
        if (response.errorMessage) {
            return;
        }

        return response.payload;
    };

    const fetchCaseFileStaticItems = async () => {
        const response = await zoneContext.zoneApiClient.getCaseFileStaticItems();
        if (response.httpResponse.status === 401) {
            window.location.reload();
            return;
        }
        if (response.errorMessage) {
            return;
        }
        return response.payload;
    };

    const fetchCaseFileDisplayCategories = async () => {
        const response = await _apiClient.getCaseFileDisplayCategories();
        if (response.httpResponse.status === 401) {
            window.location.reload();
            return;
        }
        if (response.errorMessage) {
            return;
        }
        return response.payload;
    };

    const fetchAssertionDescriptors = async () => {
        const response = await zoneContext.zoneApiClient.getAssertionDescriptors(
            AssertionDescriptorUsageTypesEnum.Case
        );
        if (response.httpResponse.status === 401) {
            window.location.reload();
            return;
        }
        if (response.errorMessage) {
            return;
        }
        return response.payload;
    };

    const fetchCaseDetailsById = async (id: string) => {
        const response = await zoneContext.zoneApiClient.getCaseById(id);
        if (response.httpResponse.status === 401) {
            window.location.reload();
            return;
        }
        if (response.errorMessage) {
            return;
        }
        return response.payload;
    };

    const fetchLoggedInUserDefaultOrganization = async () => {
        const response = await zoneContext.zoneApiClient.getCurrentUserOrganizationFromServer(
            zoneContext.zoneGuid
        );
        if (!response) return;
        if (response.httpResponse.status === 401) {
            window.location.reload();
            return;
        }
        if (response.errorMessage) {
            return;
        }
        return response.payload;
    };

    const fetchCaseAssertionsForNewCase = async () => {
        const response = await zoneContext.zoneApiClient.getAssertionDescriptors(
            AssertionDescriptorUsageTypesEnum.Case
        );
        if (response.httpResponse.status === 401) {
            window.location.reload();
            return;
        }
        if (response.errorMessage) {
            return;
        }
        const caseAssertions: ICaseAssertionModel[] = [];
        response
            .payload!.filter(
                (x) => x.valueSourceType!.id != AssertionDescriptorValueSourceTypeEnum.Implied.Value
            )
            .map((assertionDescriptor: IAssertionDescriptorModel) => {
                const caseAssertion: ICaseAssertionModel = {
                    assertionDescriptorGuid: assertionDescriptor.guid,
                };
                caseAssertions.push(caseAssertion);
            });
        return caseAssertions;
    };

    const fetchCaseStatusLookup = async () => {
        const response = await _apiClient.getCaseStatusLookupData();
        if (response.httpResponse.status === 401) {
            window.location.reload();
            return;
        }
        if (response.errorMessage) {
            throw new Error(response.errorMessage);
        }
        return response.payload;
    };

    const fetchCaseManagers = async () => {
        const response = await zoneContext.zoneApiClient.getCaseManagersInZone();
        if (response.httpResponse.status === 401) {
            window.location.reload();
            return;
        }
        if (response.errorMessage) {
            throw new Error(response.errorMessage);
        }
        return response.payload;
    };

    const fetchAllUsersInZone = async () => {
        const response = await zoneContext.zoneApiClient.getUsersInZone();
        if (response.httpResponse.status === 401) {
            window.location.reload();
            return;
        }
        if (response.errorMessage) {
            throw new Error(response.errorMessage);
        }
        return response.payload;
    };

    const fetchZoneDefenseCounsels = async () => {
        const response = await zoneContext.zoneApiClient.getOrganizationsInZone(
            OrganizationTypesEnum.Firm.Value
        );
        if (response.httpResponse.status === 401) {
            window.location.reload();
            return;
        }
        if (response.errorMessage) {
            throw new Error(response.errorMessage);
        }
        return response.payload;
    };

    const fetchCaseNoteCategoryTypes = async () => {
        const response = await zoneContext.zoneApiClient.getNoteCategoryTypes(
            LookupDataEnums.CaseNoteCategories
        );
        if (response.httpResponse.status === 401) {
            window.location.reload();
            return;
        }
        if (response.errorMessage) {
            throw new Error(response.errorMessage);
        }
        return response.payload;
    };

    const fetchOrganizationSettings = async () => {
        const response = await zoneContext.zoneApiClient.getOrganizationSettings();
        if (response.httpResponse.status === 401) {
            window.location.reload();
            return;
        }
        if (response.errorMessage) {
            throw new Error(response.errorMessage);
        }

        return response.payload;
    };

    // FUNCTIONS

    const loadInitialData = async (caseGuid?: string) => {
        if (!user.active) return;

        setUser(user);

        if (!caseGuid) setIsReadOnlyMode(false);

        let currentCase: ICaseModel = {};
        let enableCaseFileEdit = false;
        let enablePlanningExecution = false;

        if (caseGuid) {
            // Edit existing case
            const caseAPIResponse = await fetchCaseDetailsById(caseGuid);

            if (!caseAPIResponse) {
                showErrorToast('Failed to load this Case', { position: 'bottom-center' });
                setIsLoading(false);
                return;
            }
            setIsCaseLoaded(true);

            currentCase = caseAPIResponse;

            if (currentCase.organization) {
                if (currentCase.organization.enableCaseFileEdit) enableCaseFileEdit = true;
                if (currentCase.organization.enablePlanningExecution)
                    enablePlanningExecution = true;
            }
        } else {
            // Create New case
            const defaultOrganization = await fetchLoggedInUserDefaultOrganization();
            if (!defaultOrganization) return;
            if (defaultOrganization) {
                currentCase.organization = defaultOrganization;
                if (defaultOrganization.enableCaseFileEdit) enableCaseFileEdit = true;
                if (defaultOrganization.enablePlanningExecution) enablePlanningExecution = true;
            }

            const caseAssertionsForNewCase = await fetchCaseAssertionsForNewCase();
            if (!caseAssertionsForNewCase) return;
            currentCase.caseAssertions = caseAssertionsForNewCase;
        }

        // Custom Logic if Logged In User is an Employee of the Portfolio or Company organization. Always disable case File Edit and PlanningExecution
        const caseBelongsToUserOrg =
            (user.homeOrganization &&
                currentCase.organization!.primaryAssociatedOrganization?.guid ===
                    user.homeOrganization?.guid) ||
            user.homeOrganization!.guid == currentCase.organization!.guid;

        if (isSystemAdmin || caseBelongsToUserOrg) {
            enableCaseFileEdit = false;
            enablePlanningExecution = false;
        }

        setIsLoading(true);
        let hasErrors = false;
        await Promise.all([
            fetchCaseFileModel(),
            fetchCaseFileStaticItems(),
            fetchAssertionDescriptors(),
            fetchCaseStatusLookup(),
            fetchCaseManagers(),
            fetchAllUsersInZone(),
            fetchZoneDefenseCounsels(),
            fetchCaseNoteCategoryTypes(),
            fetchCaseFileDisplayCategories(),
        ])
            .then(
                ([
                    displaySections,
                    caseFileStaticItems,
                    assertionDescriptors,
                    caseStatusLookup,
                    caseManagersInZone,
                    allUsersInZones,
                    zoneDefenseCounsels,
                    caseNoteCategoryTypes,
                    displayCategories,
                ]) => {
                    setDisplaySections(displaySections ?? []);
                    setStaticItems(caseFileStaticItems ?? []);
                    setAssertionDescriptors(assertionDescriptors ?? []);
                    setCaseStatuses(caseStatusLookup ?? []);
                    setCaseManagers(caseManagersInZone ?? []);
                    setZoneUsers(allUsersInZones ?? []);
                    setZoneDefenseCounsels(zoneDefenseCounsels ?? []);
                    setCaseNoteCategoryTypes(caseNoteCategoryTypes ?? []);
                    setCaseDisplayCategories(displayCategories ?? []);

                    CaseDataCustomLogicHelper.Run(currentCase, assertionDescriptors ?? []);
                }
            )
            .catch((error: Error) => {
                hasErrors = true;
                setValidation(ValidateUtils.parseErrors([], error.message));
            })
            .finally(() => {
                setIsLoading(false);
            });

        if (hasErrors) return;

        setCurrentCase(currentCase);
        setOriginalCase(cloneDeep(currentCase));
        setOriginalNotes(cloneDeep(currentCase.notes ?? []));

        setEnableCaseFileEdit(enableCaseFileEdit);
        setEnablePlanningExecution(enablePlanningExecution);
        setIsLoading(true);

        await fetchOrganizationSettings()
            .then((orgSettings) => setOrganizationSettings(orgSettings ?? []))
            .catch((error: Error) => setValidation(ValidateUtils.parseErrors([], error.message)))
            .finally(() => {
                setIsLoading(false);
            });
    };

    const resetConfirmChanges = (originalCase: ICaseModel) => {
        setCurrentCase(cloneDeep(originalCase));
        setIsReadOnlyMode(true);
        setValidation({} as IValidation);
        setShowUnsavedChangesOnCancelDialog(false);
    };

    const getSingleValueEditControl = (caseAssertion: ICaseAssertionModel) => {
        const setStyleAsSystemAutoPopulated = CaseHelper.setStyleAsSystemAutoPopulated(
            caseAssertion,
            currentCase
        );
        const additionalStyleClass = setStyleAsSystemAutoPopulated ? ' font-green' : '';

        const foundAD = assertionDescriptors.find(
            (ad) => ad.guid === caseAssertion.assertionDescriptorGuid
        );

        if (!foundAD) {
            return null;
        }

        if (CaseHelper.isFieldReadOnly(caseAssertion?.assertionDescriptorGuid ?? '', currentCase)) {
            const value = CaseHelper.getAssertionTextByGuid(
                [caseAssertion],
                caseAssertion.assertionDescriptorValueGuid ?? '',
                [foundAD]
            );

            return (
                <input
                    className={'form-control d-inline-block ms-2' + additionalStyleClass}
                    type="text"
                    name={caseAssertion.assertionDescriptorGuid}
                    value={value}
                    readOnly={true}
                />
            );
        }

        if (foundAD.valueDisplayType?.id === AssertionDescriptorDisplayValueTypeEnum.DateTime.Value)
            return (
                <input
                    className={
                        caseAssertion.dateTimeValue &&
                        Common.isValidDate(caseAssertion.dateTimeValue)
                            ? 'form-control d-inline-block ms-2'
                            : 'form-control d-inline-block ms-2 unselectClass' +
                              additionalStyleClass
                    }
                    type="date"
                    name={caseAssertion.assertionDescriptorGuid}
                    value={Common.safeDateFormat(caseAssertion.dateTimeValue)}
                    onChange={handleChangeADTextValue}
                />
            );

        if (
            foundAD.valueDisplayType?.id == AssertionDescriptorDisplayValueTypeEnum.Percentage.Value
        )
            return (
                <span>
                    <span>%</span>
                    <input
                        className={'form-control d-inline-block ms-2' + additionalStyleClass}
                        type="number"
                        name={caseAssertion.assertionDescriptorGuid}
                        value={caseAssertion.numericValue}
                        onChange={handleChangeADTextValue}
                    />
                </span>
            );

        if (
            foundAD.valueDisplayType?.id ==
                AssertionDescriptorDisplayValueTypeEnum.Currency.Value ||
            foundAD.valueDisplayType?.id ==
                AssertionDescriptorDisplayValueTypeEnum.CurrencyWithCents.Value
        )
            return (
                <span style={{ marginLeft: '10px' }}>
                    <span>$</span>
                    <input
                        className={'form-control d-inline-block ms-2' + additionalStyleClass}
                        type="number"
                        name={caseAssertion.assertionDescriptorGuid}
                        value={caseAssertion.numericValue}
                        onChange={handleChangeADTextValue}
                    />
                </span>
            );

        if (foundAD.valueDisplayType?.id == AssertionDescriptorDisplayValueTypeEnum.Number.Value)
            return (
                <input
                    className={'form-control d-inline-block ms-2' + additionalStyleClass}
                    type="number"
                    name={caseAssertion.assertionDescriptorGuid}
                    value={caseAssertion.textValue}
                    onChange={handleChangeADTextValue}
                />
            );

        if (foundAD.valueDisplayType?.id == AssertionDescriptorDisplayValueTypeEnum.ShortText.Value)
            return (
                <input
                    className={'form-control d-inline-block ms-2' + additionalStyleClass}
                    type="text"
                    maxLength={100}
                    name={caseAssertion.assertionDescriptorGuid}
                    value={caseAssertion.textValue}
                    onChange={handleChangeADTextValue}
                />
            );

        if (foundAD.valueDisplayType?.id == AssertionDescriptorDisplayValueTypeEnum.SSN.Value)
            return (
                <input
                    className={'form-control d-inline-block ms-2' + additionalStyleClass}
                    type="text"
                    placeholder="XXX-XX-XXXX"
                    maxLength={11}
                    name={caseAssertion.assertionDescriptorGuid}
                    value={caseAssertion.textValue}
                    onChange={handleChangeADTextValue}
                />
            );

        if (foundAD.valueSourceType?.id == AssertionDescriptorValueSourceTypeEnum.Boolean.Value)
            return (
                <div className={'d-inline-block ms-2'}>
                    <CheckmateSelect
                        options={CheckmateSelectHelper.getBooleanOptions()}
                        value={CheckmateSelectHelper.getBooleanOptionValue(
                            caseAssertion.booleanValue
                        )}
                        systemPopulated={setStyleAsSystemAutoPopulated}
                        onChange={(e: any) => {
                            handleAssertionDescriptorSearchableSingleSelectChange(
                                e,
                                caseAssertion.assertionDescriptorGuid!,
                                true
                            );
                        }}
                    />
                </div>
            );

        if (foundAD.valueSourceType?.id == AssertionDescriptorValueSourceTypeEnum.Selection.Value) {
            return (
                <span className="d-inline-block ms-2">
                    <CheckmateSelect
                        options={CaseFileCheckmateSelectHelper.getOptionsForCheckmateSelect(
                            foundAD.assertionDescriptorValues!
                        )}
                        name={caseAssertion.assertionDescriptorGuid!}
                        systemPopulated={setStyleAsSystemAutoPopulated}
                        value={CaseFileCheckmateSelectHelper.getCheckmateSelectValue(
                            caseAssertion,
                            assertionDescriptors
                        )}
                        onChange={(e: any) =>
                            handleAssertionDescriptorSearchableSingleSelectChange(
                                e,
                                caseAssertion.assertionDescriptorGuid!
                            )
                        }
                    />
                </span>
            );
        }
    };

    // HANDLERS

    const handleSetPriorityDialogConfrim = async () => {
        if (!currentCase) return;

        setShowSetPriorityDialog(false);
        setIsLoading(true);
        const response = await zoneContext.zoneApiClient.updateCasePriority(
            currentCase.guid!,
            currentCase.priority ? 'false' : 'true'
        );

        if (response.httpResponse.status === 401) {
            window.location.reload();
            return;
        }

        if (response.errorMessage) {
            setValidation(ValidateUtils.parseErrors(response.errors, response.errorMessage));
            return;
        }

        const updatedCase = { ...currentCase, priority: !currentCase.priority };

        const modifiedDateCaseData = await setModifiedDate(updatedCase);
        setCurrentCase(modifiedDateCaseData);
        setOriginalCase(modifiedDateCaseData);
        setIsLoading(false);
    };

    const setModifiedDate = async (caseData: ICaseModel) => {
        const caseAPIResponse = await fetchCaseDetailsById(currentCase.guid!);
        if (caseAPIResponse) {
            caseData = { ...caseData, modifiedDate: caseAPIResponse.modifiedDate };
        }
        return caseData;
    };

    const handleSaveNoteComplete = async (noteItem: INoteModel) => {
        const caseCopy = { ...currentCase };
        const caseNotes = caseCopy.notes ?? [];
        const foundIdx = caseNotes?.findIndex((note) => note.guid === noteItem.guid);

        if (foundIdx > -1) {
            caseNotes[foundIdx] = noteItem;
        } else {
            caseNotes.push({ ...noteItem });
        }

        caseCopy.notes = caseNotes.sort(NoteHelper.sortNotesByStarredAndThenByModifiedDate);

        const originalNotes = cloneDeep(caseCopy.notes);

        setCurrentNote(null);
        const modifiedDateCaseData = await setModifiedDate(caseCopy);
        setCurrentCase(modifiedDateCaseData);
        setOriginalCase((prev) => ({
            ...prev,
            notes: originalNotes,
            modifiedDate: modifiedDateCaseData.modifiedDate,
        }));
        setOriginalNotes(originalNotes);
    };

    const handleCancelAddEditNote = () => {
        const validationCopy = { ...validation };
        validationCopy.categories = [];
        validationCopy.content = [];

        const caseCopy = { ...currentCase };
        caseCopy.notes = [...originalNotes];

        setCurrentCase(caseCopy);
        setForceNoteEditorInReadOnlyMode(false);
        setCurrentNote(null);
        setValidation(validationCopy);
    };

    const handleAddNote = () => {
        const currentNote: INoteModel = {
            type: { id: NoteTypes.CaseNote },
            status: { id: NoteStatusTypes.Open },
            caseGuid: currentCase.guid!,
            purpose: { id: NotePurposeTypesEnum.Info },
        };

        setCurrentNote(currentNote);
        setForceNoteEditorInReadOnlyMode(false);
    };

    const handleSaveCase = async () => {
        const caseCopy: ICaseModel = cloneDeep(currentCase);

        caseCopy.defenseCounsels = caseCopy.defenseCounsels?.filter((dc) => !!dc.guid);

        // CHECK-2126 - notes, depositions & Case Experts are saved by independent API calls and not part of the save case.
        caseCopy.notes = [];
        caseCopy.depositions = [];
        caseCopy.caseExperts = [];

        setIsLoading(true);
        setValidation({});

        // SSN Validation - Quick and Easy option for now
        const ssnErrors =
            caseCopy.caseAssertions?.flatMap((assertion) => {
                const ad = assertionDescriptors.find(
                    (desc) => desc.guid === assertion.assertionDescriptorGuid
                );

                const isSSN = ad?.name?.toUpperCase().includes('SSN');
                const hasInvalidSSN =
                    assertion.textValue && !ValidateUtils.isValidSSN(assertion.textValue);

                if (ad && isSSN && hasInvalidSSN) {
                    return [
                        `Invalid ${ad.displayName}. Enter a valid SSN in the format XXX-XX-XXXX (Use 0's if you only have last 4 of the SSN i.e. 000-000-1234) |`,
                    ];
                }

                return [];
            }) ?? [];

        const dateErrors =
            caseCopy.caseAssertions?.flatMap((assertion) => {
                const ad = assertionDescriptors.find(
                    (desc) => desc.guid === assertion.assertionDescriptorGuid
                );

                const isDateTime =
                    ad?.valueDisplayType?.id ===
                    AssertionDescriptorValueDisplayTypes.DateTime.Value;

                const dateValue = assertion.dateTimeValue ?? assertion.textValue ?? '';
                const hasInvalidDate = !!dateValue && !Common.isValidDate(dateValue);

                if (isDateTime && hasInvalidDate) {
                    return [`Invalid ${ad.displayName}. |`];
                }

                return [];
            }) ?? [];

        const errors: string[] = ssnErrors.concat(dateErrors);

        if (errors.length > 0) {
            const validation: IValidation = {};
            validation.model = errors;
            setIsLoading(false);
            setValidation(validation);
            return;
        }

        CaseHelper.updateUserOverrideFieldForCaseAssertions(caseCopy);

        let response: IApiResponse<ICaseModel>;

        if (caseCopy.guid) {
            response = await zoneContext.zoneApiClient.updateCase(caseCopy);
        } else {
            response = await zoneContext.zoneApiClient.createCase(caseCopy);
        }

        if (response.httpResponse.status === 401) {
            window.location.reload();
            return;
        }
        if (response.errorMessage) {
            setValidation(ValidateUtils.parseErrors(response.errors, response.errorMessage));
            setIsLoading(false);
            return;
        }

        setOriginalCase(caseCopy);

        window.location.assign(
            zoneContext.buildZoneUrl(LocalRoutes.CaseFile.replace(':guid', response.payload!.guid!))
        );
        return;
    };

    const handleCancelClick = async () => {
        if (hasUnsavedChanges) {
            setShowUnsavedChangesOnCancelDialog(true);
            return;
        }

        if (currentCase.guid) {
            setIsReadOnlyMode(true);
        } else {
            zoneContext.zoneNavigate(LocalRoutes.Cases);
        }
    };

    const handleDeleteCaseDialogConfirm = async () => {
        if (!currentCase) return;

        setValidation({});
        setIsLoading(true);

        const sameCaseCheckResponse = await _apiClient.checkCaseSameCase(currentCase.guid!);
        const sameCaseStatusTypeId = sameCaseCheckResponse.payload?.sameCaseStatusTypeId;

        if (sameCaseStatusTypeId && sameCaseStatusTypeId !== SameCaseStatusType.Rejected) {
            setIsLoading(false);
            setShowDeleteCaseDialog(false);
            if (sameCaseStatusTypeId === SameCaseStatusType.Approved) {
                setValidation(
                    ValidateUtils.parseErrors(
                        [],
                        'Cannot delete this case because it maybe be part of a Same Case Group. If unexpected please refresh the page and try again.'
                    )
                );
            } else if (sameCaseStatusTypeId === SameCaseStatusType.Pending) {
                setShowPendingSameCaseDeleteCaseDialog(true);
            }
        } else {
            await deleteCaseApiCall();
        }
    };

    const handlePendSameCaseDeleteCaseDialogConfirm = async () => {
        await deleteCaseApiCall();
    };

    const deleteCaseApiCall = async () => {
        const response = await zoneContext.zoneApiClient.deleteCase(currentCase.guid!);
        if (response.httpResponse.status === 401) {
            window.location.reload();
            return;
        }
        if (response.errorMessage) {
            setIsLoading(false);
            setValidation(ValidateUtils.parseErrors(response.errors, response.errorMessage));
            return;
        }

        zoneContext.zoneNavigate(LocalRoutes.Cases);
    };

    const handleSetCaseResolutionCase = async () => {
        if (!currentCase) return;

        setValidation({});
        setIsLoading(true);
        
        var caseResolutionPlan: CreateCaseResolutionPlanRequest = {CaseId: currentCase.guid!}
        
        const response = await _apiClient.createCaseResolutionCasePlan(caseResolutionPlan);
        
        if (response.httpResponse.status == 401) {
            window.location.reload();
            return;
        }
        
        if (response.errorMessage) {
            throw new Error(response.errorMessage);
        }
        return response.payload;
    };
    const handleCaseExpertEditorSave = (updatedCaseExpert: ICaseExpertViewDetailModel) => {
        if (!currentCase) return;

        const caseCopy = cloneDeep(currentCase);
        const found = caseCopy.caseExperts?.find((ce) => ce.guid === updatedCaseExpert.guid);

        if (found) {
            found.disciplines = updatedCaseExpert.expert.disciplines ?? [];
            found.leadDefendant = updatedCaseExpert.leadDefendant ?? '';
            found.coDefendants = updatedCaseExpert.coDefendants ?? '';
            found.expertReportStatusTypeId = updatedCaseExpert.expertReportStatusTypeId ?? 0;
            found.expertRetainedByTypeId = updatedCaseExpert.expertRetainedByTypeId ?? 0;
            found.purposeOfRetention = updatedCaseExpert.purposeOfRetention;

            setCurrentCaseExpert({ ...found } as IViewCaseExpertModelWithDefaultEdit);
        }

        setCurrentCase(caseCopy);
        setOriginalCase(caseCopy);
    };

    const handleUpdateCaseNotes = (notes: INoteModel[]) => {
        const caseCopy = cloneDeep(currentCase);
        caseCopy.notes = [...notes];
        setCurrentCase(caseCopy);
        setNotesRefreshIdentifier(uniqueId);
    };

    const handleDefenseCounsalModalSubmit = async (
        currentLocalCounsels: IAssociatedOrganizationModel[]
    ) => {
        setShowDefenseCounselModal(false);
        const updatedCase = { ...currentCase, defenseCounsels: currentLocalCounsels };
        const modifiedDateCaseData = await setModifiedDate(updatedCase);
        setCurrentCase(modifiedDateCaseData);
        setOriginalCase(modifiedDateCaseData);
    };

    // EFFFECTS

    useEffect(() => {
        if (!isLoadingUser && isLoggedIn && zoneContext.zoneGuid !== '') {
            if (
                !Authorization.isAuthorizedToRoute(LocalRoutes.CaseFile, user, zoneContext.zoneGuid)
            ) {
                zoneContext.zoneNavigate(LocalRoutes.AccessDenied, { replace: true });
                return;
            }
            loadInitialData(guid);
        }
    }, [user, isLoggedIn, isLoadingUser, zoneContext.zoneGuid]);

    // RENDER

    if (isLoading) {
        return <Loader />;
    }

    if (!isCaseLoaded && currentCase.guid) {
        return null;
    }

    if (currentCaseExpert) {
        return (
            <CaseExpertEditor
                caseExpertGuid={currentCaseExpert.guid}
                caseGuid={currentCase.guid}
                defaultIsEditMode={currentCaseExpert.defaultIsEditMode ?? false}
                onBackClick={() => setCurrentCaseExpert(null)}
                onUpdateCaseNotes={handleUpdateCaseNotes}
                onSave={handleCaseExpertEditorSave}
                user={user}
                noteRefreshIdentifier={notesRefreshIdentifier}
            />
        );
    }

    const headerSection = displaySections[0];
    const caseNarrativeAD = assertionDescriptors.find(
        (ad) => ad.guid === currentNarrativeCaseAssertion?.assertionDescriptorGuid
    );

    const showNoteOverlay = currentNote !== null;

    return (
        <>
            <DocumentTitle title={currentCase.guid ? currentCase.caseName : 'Create Case'} />
            <UnsavedChangesBlocker when={hasUnsavedChanges && !isLoading} />

            {showNoteOverlay && (
                <MainContentOverlayComponent>
                    <NoteEditor
                        caseLocalCounselGuid={CaseHelper.getCaseLocalCounselGuid(currentCase)}
                        caseTitle={CaseHelper.getCaseTitle(currentCase)}
                        currentNote={currentNote}
                        defaultAlertTarget={CaseHelper.getDefaultAlertTargetForCase(
                            user,
                            currentCase,
                            zoneContext.zoneGuid
                        )}
                        defaultWatchTargets={CaseHelper.getDefaultWatchTargetsForCase(
                            user,
                            currentCase,
                            zoneContext.zoneGuid
                        )}
                        enableNoteAssociations={false}
                        noteAssociationEntityItems={currentCase.caseExperts?.map(
                            (ce) =>
                                ({
                                    label: ce.name,
                                    value: ce.guid,
                                    guid: ce.guid,
                                }) as IMultiSelectOption
                        )}
                        noteAssociationEntityType={{ id: EntityTypes.CaseExpert }}
                        isReadonlyMode={forceNoteEditorInReadOnlyMode}
                        onCancel={handleCancelAddEditNote}
                        onSaveComplete={handleSaveNoteComplete}
                        noteAssociationTitle="Add Related Case Expert(s)"
                        noteAssociationIcon="fa-head-side-brain"
                        noteCategoryTypes={caseNoteCategoryTypes}
                        useNoteAssociationsModal
                    />
                </MainContentOverlayComponent>
            )}

            <PageWithAffixedHeader
                printHide={currentNote !== null}
                headerComponent={
                    <>
                        <CaseFileHeader
                            user={user}
                            enableCaseFileEdit={enableCaseFileEdit}
                            enablePlanningExecution={enablePlanningExecution}
                            hasUnsavedChanges={hasUnsavedChanges}
                            isSystemAdmin={isSystemAdmin}
                            onClickAddNote={handleAddNote}
                            onCaseResolutionClick={() => setShowCaseResolutionDialog(true)}
                            onClickDeleteCase={() => setShowDeleteCaseDialog(true)}
                            onClickSetCasePriority={() => setShowSetPriorityDialog(true)}
                            onDefenseCounselClick={() => setShowDefenseCounselModal(true)}
                            onHistoryClick={() => setShowHistoryModal(true)}
                            validation={validation}
                            onCancelClick={handleCancelClick}
                            onSaveClick={handleSaveCase}
                        />
                        <div style={{ padding: '10px 24px' }} className="container-fluid">
                            <CaseFileSection
                                isOpen
                                numberOfColumns={3}
                                section={headerSection}
                                user={user}
                                validation={validation}
                                displayAsCondensed={true}
                            />
                        </div>
                    </>
                }
            >
                <hr style={{ margin: 0 }} />
                <CaseFileSections
                    sections={
                        displaySections.filter((section) => section.id !== headerSection.id) ?? []
                    }
                    user={user}
                    validation={validation}
                />
            </PageWithAffixedHeader>

            {showDefenseCounselModal && (
                <CaseDefenseCounselsModal
                    onClose={() => setShowDefenseCounselModal(false)}
                    onSubmit={handleDefenseCounsalModalSubmit}
                    open={showDefenseCounselModal}
                    validation={validation}
                    readonly={isReadOnlyMode}
                    showEditButton={Authorization.userHasRight(
                        UserRightsEnum.EditCaseFile,
                        user,
                        zoneContext.zoneGuid
                    )}
                />
            )}

            {showHistory && (
            <CaseAuditControl
                onCancel={() => setShowHistoryModal(false)}
                open={showHistory}
                params={{
                    caseGuid: currentCase.guid ?? ''
                }}
                title={'Case Audit History'}
            />

            )}

            <CheckmateDialog
                isShowingModal={showSetPriorityDialog}
                body={
                    currentCase?.priority
                        ? 'Removing Priority Status on a case will remove Priority Status on all tasks for the case. Are you sure you want to remove the Priority Status on this case?'
                        : 'Are you sure you want to mark this case as Priority?'
                }
                handleClose={() => {
                    setShowSetPriorityDialog(false);
                }}
                handleConfirm={handleSetPriorityDialogConfrim}
                confirmText={(currentCase?.priority ? 'Remove' : 'Mark') + ' Priority'}
                cancelText="Cancel"
                confirmButtonClassName={'btn btn-orange'}
                closeButtonClassName="btn btn-default"
                title={(currentCase?.priority ? 'Remove' : 'Mark') + ' Priority'}
                showBackdrop
            />

            <CheckmateDialog
                isShowingModal={showCaseResolutionDialog}
                body={
                    currentCase?.priority
                        ? 'Are you sure you want to remove this case from Case Resolution? This operation is permanent and cannot be reverted back.'
                        : 'Are you sure you want to add this case to Case Resolution?'
                }
                handleClose={() => {
                    setShowCaseResolutionDialog(false);
                }}
                handleConfirm={handleSetCaseResolutionCase}
                confirmText={(currentCase?.priority ? 'Remove' : 'Mark') + ' Case Resolution'}
                cancelText="Cancel"
                confirmButtonClassName={'btn btn-orange'}
                closeButtonClassName="btn btn-default"
                title={(currentCase?.priority ? 'Remove' : 'Mark') + ' Case Resolution'}
            />

            <CheckmateDialog
                body="Are you sure you want to delete this case? This operation is permanent and cannot be reverted back."
                cancelText="Cancel"
                closeButtonClassName="btn btn-default"
                confirmButtonClassName="btn btn-danger"
                confirmText="Delete"
                handleClose={() => setShowDeleteCaseDialog(false)}
                handleConfirm={handleDeleteCaseDialogConfirm}
                isShowingModal={showDeleteCaseDialog}
                size="sm"
                title="Delete"
                showBackdrop
            />

            <CheckmateDialog
                body="This case might be a part of a same case group. Are you sure you want to delete this case? This operation is permanent and cannot be reverted back"
                cancelText="Cancel"
                closeButtonClassName="btn btn-default"
                confirmButtonClassName="btn btn-danger"
                confirmText="Confirm"
                handleClose={() => setShowPendingSameCaseDeleteCaseDialog(false)}
                handleConfirm={handlePendSameCaseDeleteCaseDialogConfirm}
                isShowingModal={showPendingSameCaseDeleteCaseDialog}
                size="sm"
                title="Pending Same Case"
                showBackdrop
            />

            <CheckmateDialog
                isShowingModal={showUnsavedChangesOnCancelDialog}
                title={'Unsaved Changes Detected'}
                body={'You have unsaved changes. Are you sure you want to proceed?'}
                handleClose={() => {
                    setShowUnsavedChangesOnCancelDialog(false);
                }}
                handleConfirm={() => {
                    setCurrentCase(cloneDeep(originalCase));
                    setShowUnsavedChangesOnCancelDialog(false);

                    if (currentCase.guid) {
                        setIsReadOnlyMode(true);
                        resetConfirmChanges(originalCase);
                    } else {
                        setTimeout(() => {
                            zoneContext.zoneNavigate(LocalRoutes.Cases);
                        }, 1);
                    }
                }}
                cancelText={'Cancel'}
                confirmText={'Continue'}
                closeButtonClassName="btn btn-default"
                confirmButtonClassName="btn btn-black"
                size="sm"
                showBackdrop
            />

            <AdditionalFieldsDialog
                assertionDescriptors={assertionDescriptors}
                currentCase={currentCase}
                getSingleValueEditControl={getSingleValueEditControl}
                onHide={() => setShowAdditionalFieldsDialog(false)}
                open={showAdditionalFieldsDialog}
                validation={validation}
                onCancelClick={() => setShowAdditionalFieldsDialog(false)}
                onOkClick={() => setShowAdditionalFieldsDialog(false)}
            />

            {currentNarrativeCaseAssertion && (
                <CaseNarrativeModal
                    title={caseNarrativeAD?.displayName ?? ''}
                    assertion={currentNarrativeCaseAssertion}
                    validation={validation}
                    onSubmit={(value: string) => {
                        handleNarrativeChange(
                            currentNarrativeCaseAssertion?.assertionDescriptorGuid ?? '',
                            value
                        );
                        setCurrentNarrativeCaseAssertion(null);
                    }}
                    onCancel={() => setCurrentNarrativeCaseAssertion(null)}
                />
            )}
        </>
    );
}
